// Gate centralizado para doctoras de prueba (`is_test_professional`).
//
// Política (definida con el equipo, 2026-05-25):
//   - En producción, con un usuario normal → NUNCA mostrar doctoras de prueba.
//   - Excepción: localhost (dev), o usuario interno/de prueba de Daisies,
//     identificado por `dais.user.user_type` ∈ {dais_test, dais_intern}.
//
// El flag `is_test_professional` vive en el modelo `dais.professional` y el
// backend lo incluye en cada objeto profesional de las respuestas de horarios
// y directorio. Este módulo es la única fuente de verdad para decidir si una
// doctora de prueba se renderiza: todos los servicios deben pasar por aquí.

const USER_KEY = 'daisies_user_data'; // debe coincidir con authService

// `user_type` no llega al doc Firestore `dais_user` ni a `dais.user.get_model_data`,
// así que la web no puede saber directamente si la sesión es dais_test/dais_intern.
// En su lugar el backend es la autoridad: los endpoints user-scoped que pasan por
// `dais.user.get_professional_ids` (get_professional_sequence_for_user,
// create_include_consultation_by_user_v2, process_get_professional_by_categories)
// SOLO incluyen doctoras `is_test_professional` cuando el user_type lo permite.
// Si una de esas respuestas trae una doctora de prueba, persistimos el acceso aquí.
const TEST_ACCESS_KEY = 'daisies_backend_test_access';

const INTERNAL_USER_TYPES = new Set(['dais_test', 'dais_intern']);

// ¿Estamos corriendo en local (dev)?
export const isLocalEnv = () => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname || '';
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '0.0.0.0' ||
    h === '::1' ||
    h.endsWith('.local')
  );
};

// ¿El usuario logueado es interno/de prueba de Daisies?
// Se lee directo de localStorage para evitar import circular con authService.
export const isInternalUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return false;
    const u = JSON.parse(raw);
    const type = u?.user_type ?? u?.userType ?? null;
    return type != null && INTERNAL_USER_TYPES.has(String(type));
  } catch (e) {
    return false;
  }
};

// Acceso confirmado por el backend en esta sesión (ver TEST_ACCESS_KEY arriba).
const hasBackendTestAccess = () => {
  try {
    return localStorage.getItem(TEST_ACCESS_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const grantBackendTestAccess = () => {
  try {
    localStorage.setItem(TEST_ACCESS_KEY, '1');
  } catch (e) { /* storage no disponible */ }
};

// Llamar en logout y al iniciar sesión con otra cuenta.
export const clearBackendTestAccess = () => {
  try {
    localStorage.removeItem(TEST_ACCESS_KEY);
  } catch (e) { /* storage no disponible */ }
};

// Escanea una respuesta user-scoped (ya gateada por el backend vía
// get_professional_ids) buscando doctoras de prueba. Si aparece alguna, el
// backend autorizó a esta sesión a verlas → persistimos el acceso ANTES de que
// los parsers/filtros locales corran, para que esa misma respuesta se renderice.
// NO usar con respuestas públicas/externals (no están gateadas por usuario).
export const noteTestProfessionalsFromUserScopedResponse = (payload) => {
  if (hasBackendTestAccess()) return; // ya confirmado, evitar re-escaneo
  let found = false;
  const walk = (node, depth) => {
    if (found || depth > 6 || node == null) return;
    if (Array.isArray(node)) {
      for (const item of node) walk(item, depth + 1);
      return;
    }
    if (typeof node !== 'object') return;
    if (node.is_test_professional === true) {
      found = true;
      return;
    }
    for (const value of Object.values(node)) walk(value, depth + 1);
  };
  walk(payload, 0);
  if (found) grantBackendTestAccess();
};

// ¿La sesión actual es de un usuario de pruebas/interno de Daisies?
// A diferencia de canSeeTestProfessionals NO incluye isLocalEnv(): correr en
// localhost no convierte a una cuenta normal en cuenta de pruebas. Sí incluye
// el acceso confirmado por backend (solo se otorga a sesiones dais_test/
// dais_intern, ver TEST_ACCESS_KEY) para cubrir logins donde user_type no
// llegó al storage. Usado p.ej. en el saludo del home ("Usuario de pruebas").
export const isTestUserSession = () => isInternalUser() || hasBackendTestAccess();

// ¿Debemos MOSTRAR doctoras de prueba en este contexto?
export const canSeeTestProfessionals = () =>
  isLocalEnv() || isInternalUser() || hasBackendTestAccess();

// Detecta una doctora de prueba de forma robusta (objeto con el flag en true).
export const isTestProfessional = (p) =>
  !!(p && typeof p === 'object' && p.is_test_professional === true);

// Filtra doctoras de prueba de una lista, salvo que el contexto permita verlas.
export const filterTestProfessionals = (list) => {
  if (!Array.isArray(list)) return list;
  if (canSeeTestProfessionals()) return list;
  return list.filter((p) => !isTestProfessional(p));
};
